<template>
  <div class="results-widget-section">
    <div :class="titleClasses">{{ title }}</div>

    <div v-if="errorMessage" class="error-label text-center">{{ errorMessage }}</div>

    <div :class="['data-description', {['text-center']: isAutoconnect}]">
      updates every 30 minutes, last updated {{ lastUpdatedTime }}
    </div>
    <slot/>
    <table class="track-table">
      <thead>
      <tr>
        <th class="text-center hide-for-small-only">{{ totalSentText }}</th>
        <th class="text-center hide-for-small-only">sms sent</th>
        <th class="text-center hide-for-small-only">app sent</th>
        <th v-if="!isAutoconnect && emailSent" class="text-center hide-for-small-only">emails sent</th>
        <th class="text-center">total clicks</th>
        <th class="text-center hide-for-small-only">total visits</th>
        <th class="text-center">visitor spend</th>
        <th class="text-center">spend/recipient</th>
        <th class="text-center hide-for-small-only">extension</th>
        <th class="text-center hide-for-small-only">total actions</th>
        <th v-if="isAutoconnect && attachedMedia" class="text-center">{{ attachedMedia.label }}</th>
        <th v-if="isAutoconnect && hasNotificationImage">notification image</th>
      </tr>
      </thead>
      <tbody>
      <tr>
        <td class="text-center hide-for-small-only sms-sent">{{ totalSent }}</td>
        <td class="text-center hide-for-small-only sms-sent">{{smsSent}}</td>
        <td class="text-center hide-for-small-only sms-sent">{{pushSent}}</td>
        <td v-if="!isAutoconnect && emailSent" class="text-center hide-for-small-only sms-sent">{{emailSent}}</td>
        <td class="text-center clicks-calculated">
          <span v-if="isAutoconnect">{{ clicksCalc }}</span>
          <a v-else @click="openClicksModal">{{ clicksCalc }}</a>
        </td>
        <td class="text-center hide-for-small-only visits-calculated">{{ visitsCalc }}</td>
        <td class="text-center visitor-spend">{{ visitSpend }}</td>
        <td class="text-center spend-recipient">{{ spendRecipient }}</td>
        <td class="text-center hide-for-small-only action-type">{{ rewardType || extensionName }}</td>
        <td class="text-center hide-for-small-only redemptions-calculated">{{ redemptionsCalc }}</td>
        <td v-if="isAutoconnect && attachedMedia" class="text-center visitor-spend">{{ attachedMedia.media }}</td>
        <td v-if="isAutoconnect && hasNotificationImage" class="text-center">{{ hasNotificationImage }}</td>
        <td class="text-center export-action" :class="{'hidden': hideExportAction}" @click="showExportModal = true">export</td>
      </tr>
      </tbody>
    </table>

    <table v-if="isEmail" class="track-table">
      <thead>
      <tr>
        <th class="text-center hide-for-small-only">emails sent</th>
        <th class="text-center hide-for-small-only">bounced</th>
        <th class="text-center">delivered</th>
        <th class="text-center">opened</th>
        <th class="text-center hide-for-small-only">unsubscribed</th>
      </tr>
      </thead>
      <tbody>
      <tr>
        <td class="text-center hide-for-small-only email-sent">{{ emailTotalSent }}</td>
        <td class="text-center hide-for-small-only bounced">{{ emailBounced }}</td>
        <td class="text-center delivered-calculated">{{ emailDeliveredCalc }}</td>
        <td class="text-center opened-calculated">{{ emailOpenedCalc }}</td>
        <td class="text-center hide-for-small-only unsubscribed">{{ emailUnsubscribed }}</td>
      </tr>
      </tbody>
    </table>

    <modal
      v-if="showClicksModal"
      :showClose="true"
      @close="showClicksModal = false"
      wrapperClasses="clicks-modal">
      <h2 slot="header" class="title">campaign #{{ campaignId }}</h2>
      <div slot="body" class="body">
        <h3>This campaign was sent to {{ totalSent }} {{ totalSent | pluralize('recipient') }}. Send a campaign to
          members based on how they reacted to your campaign</h3>
      </div>
      <div slot="action" class="action-container">
        <div class="actions">
          {{ clicksCount }} {{ clicksCount | pluralize('member') }} <b>clicked</b> on the campaign
          <button class="button larger modal-click-action" @click="redirectToNewCampaignPage('campaign_click')">send
            {{ clicksCount | pluralize('this member', 'these members') }} a campaign
          </button>
        </div>
        <div class="actions">
          {{ didNotClickCount }} {{ didNotClickCount | pluralize('member') }} <b>did not click</b> on the campaign
          <button class="button larger modal-click-action" @click="redirectToNewCampaignPage('no_campaign_click')">send
            {{ didNotClickCount | pluralize('this member', 'these members') }} a campaign
          </button>
        </div>
      </div>
    </modal>

    <modal
      v-if="showExportModal"
      class="width-custom"
      :showClose="true"
      @close="showExportModal = false"
      wrapperClasses="clicks-modal">
      <h2 slot="header" class="title">export campaign data</h2>
      <div slot="body" class="body">
        <h3>are you sure you would like to export <br> data from campaign #{{ $route.params.id }}</h3>
      </div>
      <div slot="action" class="action-container">
        <div class="btn-action export"
             v-if="!reportLink"
             :class="{disable: disableExportBtnText}"
             @click="exportCampaign">{{ exportBtnText }}
        </div>
        <a v-else
           @click="showExportModal = false"
           class="btn-action download"
           :href="reportLink">download</a>
        <div class="cancel-btn"
             @click="showExportModal = false">cancel
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import AxiosMixin from '../../mixins/AxiosMixin'
import {isObject} from '../../utilities'
import {mapState} from 'vuex'
import Modal from '../../components/shared/Modal'
import moment from 'moment-timezone'
import {campaignAPI} from "../../services/api/campaignAPI"

export default {
  props: {
    attachedMedia: Object,
    extensionName: String,
    hasNotificationImage: String,
    isAutoconnect: {
      default: false,
      type: Boolean
    },
    isEmail: {
      default: false,
      type: Boolean
    },
    isSms: {
      default: true,
      type: Boolean
    },
    isPush: {
      default: true,
      type: Boolean
    },
    isTracked: Boolean,
    rewardType: String,
    titleClasses: {
      default: 'data-title',
      type: String
    },
    totalSentText: {
      default: 'messages sent',
      type: String
    },
    hideExportAction: {
      type: Boolean,
      default: false
    },
    url: String,
    trackingDuration: null
  },

  name: 'StatsWidget',

  components: {
    Modal
  },

  mixins: [AxiosMixin],

  data() {
    return {
      clicksCount: 'calculating',
      clicksPercentage: null,
      emailTotalSent: 'calculating',
      emailBounced: 'calculating',
      emailClicked: 'calculating',
      emailClickedPercentage: null,
      emailDelivered: 'calculating',
      emailDeliveredPercentage: null,
      emailOpened: 'calculating',
      emailOpenedPercentage: null,
      emailUnsubscribed: 'calculating',
      errorMessage: null,
      errorText: 'we encountered an issue loading the campaign stats.',
      lastUpdated: null,
      redemptionCount: 'calculating',
      redemptionPercentage: null,
      showClicksModal: false,
      showExportModal: false,
      totalSent: 'calculating',
      smsSent: 'calculating',
      emailSent: 'calculating',
      pushSent: 'calculating',
      visitCount: 'calculating',
      visitCountPercentage: null,
      visitSpend: 'calculating',
      spendRecipient: 'calculating',
      visitCalculations: null,
      reportLink: '',
      exportBtnText: 'export',
      disableExportBtnText: false
    }
  },

  created() {
    this.loadStats()
  },

  watch: {
    trackingDuration: function () {
      this.loadStats()
    }
  },

  computed: {
    campaignId() {
      return this.$route.params.id
    },

    clicksCalc: function () {
      return this.configureStatsString('clicksCount', 'clicksPercentage')
    },

    didNotClickCount() {
      return this.totalSent - this.clicksCount
    },

    lastUpdatedTime: function () {
      if (this.lastUpdated && moment.tz) {
        return moment.tz(this.lastUpdated, this.currentMerchant.merchant_time_zone).format('h:mma')
      }
    },

    redemptionsCalc: function () {
      return this.configureStatsString('redemptionCount', 'redemptionPercentage')
    },

    title() {
      return this.isAutoconnect ? 'message results' : 'campaign results'
    },

    visitsCalc: function () {
      return this.configureStatsString('visitCount', 'visitCountPercentage')
    },

    emailDeliveredCalc: function () {
      if (this.emailOpened && this.emailOpenedPercentage) {
        return this.configureStatsString('emailDelivered', 'emailDeliveredPercentage')
      } else {
        return '--'
      }
    },

    emailOpenedCalc: function () {
      if (this.emailOpened && this.emailOpenedPercentage) {
        return this.configureStatsString('emailOpened', 'emailOpenedPercentage')
      } else {
        return '--'
      }
    },

    ...mapState([
      'currentMerchant'
    ]),
  },

  methods: {
    configureStatsString(count, percentage) {
      const showNoClicks = count === 'clicksCount' && !this.isTracked,
        showNoRedemptions = count === 'redemptionCount' && (!this.rewardType || this.rewardType === 'instant points'),
        showDashes = showNoClicks || showNoRedemptions
      let statsString = 'calculating'
      //if (showDashes) { return '--' }
      if (this.lastUpdated) {
        statsString = `${this[count]}`
      }
      return statsString
    },

    loadStats() {
      this.url && this.axiosMethod({
        url: this.url,
        params: { params: { duration: this.trackingDuration } },
        onSuccess: this.setResultsData,
        onError: this.setLoadError
      })
    },

    openClicksModal() {
      this.showClicksModal = true
    },

    async redirectToNewCampaignPage(segment_type) {
      const resp = await campaignAPI.postCreateCampaignWithSegments(this.campaignId, segment_type)
      location.assign(resp.edit_path)
    },

    setLoadError(error) {
      this.errorMessage = `${this.errorText} ${error}`
    },

    setResultsData(data) {
      if (data && isObject(data)) {
        this.clicksCount = data?.clicks?.count || 0;
        this.clicksPercentage = data?.clicks?.percentage
        this.emailTotalSent = data?.email_stats?.sent
        this.emailBounced = data?.email_stats?.bounced
        this.emailClicked = data?.email_stats?.clicked // click data is returned but not used yet
        this.emailClickedPercentage = data?.email_stats?.clicked_percent // click data is returned but not used yet
        this.emailDelivered = data?.email_stats?.delivered
        this.emailDeliveredPercentage = data?.email_stats?.delivered_percent
        this.emailOpened = data?.email_stats?.opened
        this.emailOpenedPercentage = data?.email_stats?.opened_percent
        this.emailUnsubscribed = data?.email_stats?.unsubscribed
        this.redemptionCount = data?.redemptions_rate_calculation?.count
        this.redemptionPercentage = data?.redemptions_rate_calculation?.percentage
        this.visitCount = data?.visits_calculations?.count
        this.visitCountPercentage = data?.visits_calculations?.count_percentage
        this.visitSpend = data?.visits_calculations?.spend
        this.spendRecipient = data?.visits_calculations?.spend_recipient_average
        this.pushSent = data.push_sent ? data.push_sent : 0
        this.emailSent = data.email_sent ? data.email_sent : 0
        this.smsSent = data.sms_sent ? data.sms_sent : 0
        this.totalSent = this.pushSent + this.emailSent + this.smsSent
        this.$emit('setCreditsUsed', data.total_credits)
        this.lastUpdated = data.stats_at
        this.errorMessage = null
      } else {
        this.errorMessage = this.errorText
      }
    },

    exportCampaign() {
      const url = '/api/v3/reports/'
      const params = {
        report: {
          type: 'Reports::CampaignExportReport',
          input_data: {
            event_value: this.$route.params.id,
            event_range_min: '',
            event_range_max: '',
            duration: this.trackingDuration
          }
        }
      }
      this.axiosMethod({
        methodType: 'post',
        url: url,
        params: params,
        onSuccess: this.checkResponseStatus,
        onError: this.setLoadError
      })
    },

    checkResponseStatus(res) {
      this.disableExportBtnText = true
      this.exportBtnText = 'waiting...'
      const url = `/api/v3/reports/${res.report.id}?report[type]=Reports::CampaignExportReport`
      let interval = setInterval(() => {
        this.axiosMethodPromise({
          url: url,
        }).then(res => {
          if (res.data.report.download_url) {
            this.reportLink = res.data.report.download_url
            clearInterval(interval)
          }
          // add countdown to btn
          if (res.data.report.estimated_run_time_remaining) this.exportBtnText = res.data.report.estimated_run_time_remaining
          else {
            clearInterval(interval)
            this.exportBtnText = 'export again'
          }
          this.disableExportBtnText = false
        }).catch(err => {
          this.showExportModal = false
          clearInterval(interval)
          this.setLoadError(err)
          this.disableExportBtnText = false
        })
      }, 1000)
    },
  }
}
</script>

<style lang="scss">
.results-widget-section {
  .title {
    font-size: 22px;
  }

  h3 {
    font-size: 18px !important;
  }

  .export-action {
    color: #128ff9;
    cursor: pointer;
  }

  .action-container {
    margin-bottom: 10px;
    display: flex;
    flex-direction: column;

    .btn-action {
      width: 100px;
      height: 40px;
      background-color: #128ff9;
      color: white !important;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      border-radius: 6px;

      &.disable {
        pointer-events: none;
      }

      &.export,
      &.download {
        display: flex;
      }
    }
    .cancel-btn {
      color: #128ff9;
      margin-top: 15px;
    }
  }

  .width-custom {
    .modal {
      width: 320px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
    }
  }
}
</style>
